import { useState } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';
import * as XLSX from 'xlsx';

const TYPES = [
  { key: 'cafe', label: '카페', color: '#00D4AA' },
  { key: 'kin', label: '지식인', color: '#378ADD' },
  { key: 'blog', label: '블로그', color: '#F6AD55' },
];

export default function SearchMulti() {
  const checkAuth = useAuthGuard();
  const [keywords, setKeywords] = useState<string[]>(Array(30).fill(''));
  const [searchType, setSearchType] = useState<'cafe'|'kin'|'blog'>('cafe');
  const [results, setResults] = useState<{keyword: string, urls: any[], status: string}[]>([]);
  const [loading, setLoading] = useState(false);

  const validKeywords = keywords.map(k => k.trim()).filter(Boolean);

  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target?.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
        const kws = rows.map(r => String(r[0] || '').trim()).filter(Boolean);
        if (kws.length > 30) {
          alert(`최대 30개까지 가능합니다. (현재 ${kws.length}개)`);
          return;
        }
        const newKeywords = [...kws, ...Array(30).fill('')].slice(0, 30);
        setKeywords(newKeywords);
      } catch { alert('엑셀 파일 읽기 실패'); }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const handleSearch = async () => {
    if (!checkAuth()) return;
    if (!validKeywords.length) return;
    setLoading(true);
    const init = validKeywords.map(k => ({ keyword: k, urls: [], status: 'loading' }));
    setResults(init);
    for (const kw of validKeywords) {
      try {
        const res = await client.post(`/search/${searchType}`, { keyword: kw });
        setResults(prev => prev.map(r => r.keyword === kw ? { ...r, urls: res.data.urls, status: 'done' } : r));
      } catch (err: any) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || '';
        if (status === 429) {
          setResults(prev => prev.map(r => r.keyword === kw ? { ...r, status: 'error' } : r));
          if (window.confirm(detail + '\n\n업그레이드하시겠습니까?')) {
            window.location.href = '/pricing';
          }
          break;
        }
        setResults(prev => prev.map(r => r.keyword === kw ? { ...r, status: 'error' } : r));
      }
    }
    setLoading(false);
  };

  const handleCopyAll = () => {
    const lines = results.flatMap(r => r.urls.map((u: any) => typeof u === 'string' ? u : u.url));
    navigator.clipboard.writeText(lines.join('\n'));
    alert('복사 완료!');
  };

  const handleExcel = () => {
    const rows = [['키워드', 'URL', '카페ID', '게시글ID']];
    results.forEach(r => {
      r.urls.forEach((u: any) => {
        if (typeof u === 'string') rows.push([r.keyword, u, '', '']);
        else rows.push([r.keyword, u.url, u.cafe_id || '', u.article_id || '']);
      });
    });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `search_${searchType}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const s: any = {
    wrap: { maxWidth: 1100 },
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 16 },
    input: { width: '100%', background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 6, padding: '7px 10px', color: '#E8ECF4', fontSize: 13, boxSizing: 'border-box' },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '7px 16px', color: '#8892A4', fontSize: 12, cursor: 'pointer' },
  };

  return (
    <Layout>
      <div style={s.wrap}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#E8ECF4', fontSize: 18, fontWeight: 700 }}>다중 키워드 수집</div>
          <div style={{ color: '#8892A4', fontSize: 12, marginTop: 4 }}>키워드별 카페/지식인/블로그 URL을 한 번에 수집합니다</div>
        </div>

        <div style={s.card}>
          {/* 수집 유형 선택 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: '#8892A4', fontSize: 11, marginBottom: 8 }}>수집 유형</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {TYPES.map(t => (
                <button key={t.key} onClick={() => setSearchType(t.key as any)} style={{
                  background: searchType === t.key ? t.color + '22' : 'none',
                  border: `1px solid ${searchType === t.key ? t.color : '#2A3045'}`,
                  borderRadius: 6, padding: '6px 18px', color: searchType === t.key ? t.color : '#8892A4',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer'
                }}>{t.label}</button>
              ))}
            </div>
          </div>

          {/* 키워드 입력 그리드 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ color: '#8892A4', fontSize: 11 }}>키워드 입력 (최대 30개)</div>
            <label style={{ background: '#1A2035', border: '0.5px solid #2A3045', borderRadius: 6, padding: '5px 12px', color: '#8892A4', fontSize: 12, cursor: 'pointer' }}>
              📂 엑셀 업로드
              <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleExcelUpload} />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 16 }}>
            {keywords.map((kw, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ color: '#4A5568', fontSize: 10, width: 16, textAlign: 'right', flexShrink: 0 }}>{i+1}</span>
                <input
                  style={s.input}
                  value={kw}
                  onChange={e => { const n = [...keywords]; n[i] = e.target.value; setKeywords(n); }}
                  placeholder={`키워드 ${i+1}`}
                />
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button style={s.btn} onClick={handleSearch} disabled={loading || !validKeywords.length}>
              {loading ? '수집 중...' : `${validKeywords.length}개 키워드 수집`}
            </button>
            {results.length > 0 && (
              <>
                <button style={s.btnGhost} onClick={handleCopyAll}>전체 URL 복사</button>
                <button style={s.btnGhost} onClick={handleExcel}>엑셀 다운로드</button>
              </>
            )}
          </div>
        </div>

        {/* 카페 TOP 5 */}
        {searchType === 'cafe' && results.some(r => r.status === 'done' && r.urls.length > 0) && (() => {
          const cafeCounts: Record<string, number> = {};
          results.forEach(r => {
            if (r.status === 'done') {
              r.urls.forEach((u: any) => {
                const cafeId = typeof u === 'string' ? u.match(/cafe\.naver\.com\/([^\/]+)/)?.[1] : u.cafe_id;
                if (cafeId && !/^\d+$/.test(cafeId)) {
                  cafeCounts[cafeId] = (cafeCounts[cafeId] || 0) + 1;
                }
              });
            }
          });
          const top5 = Object.entries(cafeCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
          if (!top5.length) return null;
          const maxCount = top5[0][1];
          return (
            <div style={{ ...s.card, marginBottom: 16 }}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 700, marginBottom: 16 }}>🏆 카페 노출 TOP 5</div>
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
                {top5.map(([cafeId, count], i) => {
                  const medals = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣'];
                  const colors = ['#F6AD55', '#8892A4', '#CD7F32', '#4A5568', '#4A5568'];
                  return (
                    <div key={cafeId} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 16, width: 24, flexShrink: 0 }}>{medals[i]}</span>
                      <a href={`https://cafe.naver.com/${cafeId}`} target="_blank" rel="noreferrer"
                        style={{ color: colors[i], fontSize: 13, fontWeight: 600, width: 160, flexShrink: 0, textDecoration: 'none' }}>
                        {cafeId}
                      </a>
                      <div style={{ flex: 1, background: '#0F1117', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                        <div style={{ width: `${(count / maxCount) * 100}%`, height: '100%', background: colors[i], borderRadius: 4 }} />
                      </div>
                      <span style={{ color: colors[i], fontSize: 13, fontWeight: 700, width: 40, textAlign: 'right' as const }}>{count}회</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ color: '#4A5568', fontSize: 11, marginTop: 12 }}>
                전체 {Object.keys(cafeCounts).length}개 카페 중 가장 많이 노출된 카페 순위입니다
              </div>
            </div>
          );
        })()}

        {/* 결과 */}
        {results.map(r => (
          <div key={r.keyword} style={s.card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{r.keyword}</div>
                {r.status === 'loading' && <span style={{ color: '#8892A4', fontSize: 12 }}>수집 중...</span>}
                {r.status === 'done' && <span style={{ background: '#0F2A22', color: '#00D4AA', fontSize: 11, padding: '2px 8px', borderRadius: 99 }}>{r.urls.length}건</span>}
                {r.status === 'error' && <span style={{ color: '#F56565', fontSize: 12 }}>오류</span>}
              </div>
              {r.status === 'done' && r.urls.length > 0 && (
                <button style={s.btnGhost} onClick={() => {
                  const lines = r.urls.map((u: any) => typeof u === 'string' ? u : u.url);
                  navigator.clipboard.writeText(lines.join('\n'));
                }}>복사</button>
              )}
            </div>
            {r.status === 'done' && r.urls.length === 0 && (
              <div style={{ color: '#4A5568', fontSize: 13 }}>결과 없음</div>
            )}
            {r.urls.map((u: any, i: number) => {
              const url = typeof u === 'string' ? u : u.url;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid #1E2535' }}>
                  <span style={{ color: '#4A5568', fontSize: 11, width: 24, textAlign: 'right', flexShrink: 0 }}>{i+1}</span>
                  <a href={url} target="_blank" rel="noreferrer" style={{ color: '#8892A4', fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</a>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Layout>
  );
}
